/**
 * Context for dynamic page titles.
 *
 * Pages can set a custom title that the Header reads for
 * dynamic routes (e.g., mint detail showing the mint name).
 *
 * Tier: 1 (beginner) — used by shared Header.
 *
 * @module contexts/PageTitleContext
 */

import { createContext, useContext, useState, useCallback, useMemo } from 'react';

interface PageTitleContextValue {
  /** Current dynamic page title, or null for default. */
  readonly title: string | null;
  /** Set a dynamic page title. Call with null to reset. */
  readonly setTitle: (title: string | null) => void;
}

const PageTitleContext = createContext<PageTitleContextValue>({
  title: null,
  setTitle: () => undefined,
});

/** Props for PageTitleProvider. */
interface PageTitleProviderProps {
  readonly children: React.ReactNode;
}

/**
 * Provider for dynamic page titles.
 *
 * @param props - Children to render
 * @returns Provider element
 */
export function PageTitleProvider({ children }: PageTitleProviderProps): React.ReactElement {
  const [title, setTitleState] = useState<string | null>(null);
  const setTitle = useCallback((t: string | null) => setTitleState(t), []);
  const value = useMemo(() => ({ title, setTitle }), [title, setTitle]);

  return <PageTitleContext.Provider value={value}>{children}</PageTitleContext.Provider>;
}

/**
 * Read the current dynamic page title.
 *
 * @returns Current title or null
 */
export function usePageTitle(): string | null {
  return useContext(PageTitleContext).title;
}

/**
 * Set a dynamic page title. Call in useEffect to update on mount.
 *
 * @returns Setter function
 */
export function useSetPageTitle(): (title: string | null) => void {
  return useContext(PageTitleContext).setTitle;
}
